import {
  flexRender,
  type Column,
  type ColumnOrderState,
  type Header,
  type Row,
  type Table,
} from '@tanstack/react-table';
import React, { FC, forwardRef, type CSSProperties, type ReactNode } from 'react';
import { Table as BootstrapTable } from 'react-bootstrap';
import { Dropdown } from 'react-bootstrap';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { FaArrowDown, FaArrowUp, FaBars, FaMagnet } from 'react-icons/fa';
import LoadingOverlay from '@achmadk/react-loading-overlay';

import './table-paginated-v8.scss';
import GridFooter from '../../grid/grid-footer';
import type { ColumnPersistenceSnapshot } from './hooks/use-column-persistence';
import type { GridColumnDef, TablePaginatedV8CoreProps } from './table-types';
import type { TableStore } from './table-store';

const defTableProps = {
  striped: true,
  hover: true,
  bordered: true,
  className: 'mb-0',
};

const defHeaderGroupProps = () => ({ className: 'text-center' });

const CustomToggle = forwardRef<HTMLAnchorElement, { onClick?: (e: React.MouseEvent) => void }>(
  ({ onClick }, ref) => (
    <div className="dot" style={{ paddingTop: '4px' }}>
      <a
        href=""
        ref={ref}
        onClick={(e) => {
          e.preventDefault();
          onClick?.(e);
        }}
        style={{ color: 'black' }}
      >
        <FaBars color="#0000CD" style={{ marginTop: '-6px' }} />
      </a>
    </div>
  ),
);
CustomToggle.displayName = 'CustomToggle';

function reorderColumn(
  draggedColumnId: string,
  targetColumnId: string,
  columnOrder: readonly string[],
): ColumnOrderState {
  const mutableOrder = [...columnOrder];
  mutableOrder.splice(
    mutableOrder.indexOf(targetColumnId),
    0,
    mutableOrder.splice(mutableOrder.indexOf(draggedColumnId), 1)[0] as string,
  );
  return mutableOrder;
}

const DraggableColumnHeader: FC<{
  table: Table<any>;
  header: Header<any, unknown>;
  index: number;
  headers: readonly Header<any, unknown>[];
  saveColumns: (state: Partial<ColumnPersistenceSnapshot>) => void;
  enableColumnSelector: boolean;
}> = ({ table, header, index, headers, saveColumns, enableColumnSelector }) => {
  const { getState, setColumnOrder } = table;
  const { columnOrder } = getState();
  const { column } = header;

  const [, dropRef] = useDrop({
    accept: 'column',
    drop: (draggedColumn: Column<any>) => {
      setColumnOrder(reorderColumn(draggedColumn.id, column.id, columnOrder));
    },
  });

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    item: () => column,
    type: 'column',
  });

  let colSelectorIdx = 0;
  if (headers[0]?.id === 'select') colSelectorIdx = 1;
  const showColumnSelector = index === colSelectorIdx;

  return (
    <th
      ref={dropRef}
      key={header.id}
      colSpan={header.colSpan}
      style={{
        opacity: isDragging ? 0.5 : 1,
        minWidth: header.getSize() ? header.getSize() : 0,
        fontWeight: '450',
        lineHeight: '1.2',
        padding: '0.15rem 0.25rem 0.25rem 0.2rem',
      }}
    >
      <div ref={previewRef}>
        {showColumnSelector && enableColumnSelector && (
          <ColumnSelectorIcon table={table} saveColumns={saveColumns} />
        )}
        {header.isPlaceholder ? null : (
          <div
            className={
              header.column.getCanSort() ? 'enable-col-sorting' : 'disable-col-sorting'
            }
            onClick={header.column.getToggleSortingHandler()}
          >
            {flexRender(header.column.columnDef.header, header.getContext())}
            {{
              asc: <FaArrowUp style={{ marginLeft: '0.3rem', marginTop: '-4px' }} />,
              desc: <FaArrowDown style={{ marginLeft: '0.3rem', marginTop: '-4px' }} />,
            }[header.column.getIsSorted() as string] ?? null}
            <span ref={dragRef} className="resizer resizer-order ms-3">
              <FaMagnet
                style={{ marginLeft: '0.3rem', marginTop: '-4px', marginRight: '4px' }}
              />
            </span>
          </div>
        )}
        <div
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          className={`resizer resizer-column show-onhover ${
            header.column.getIsResizing() ? 'isResizing' : ''
          }`}
        />
      </div>
    </th>
  );
};

const ColumnSelectorIcon: FC<{
  table: Table<any>;
  saveColumns: (state: Partial<ColumnPersistenceSnapshot>) => void;
}> = ({ table }) => {
  const { i18n, t } = useTranslation();
  const [custMenuIsOpen, setCustMenuIsOpen] = React.useState(false);

  return (
    <div
      className="show-onhover"
      style={{ position: 'absolute', left: '2px', top: '0px', marginTop: '2px' }}
    >
      <Dropdown
        className={!custMenuIsOpen ? 'show-onhover' : undefined}
        onToggle={(isOpen) => setCustMenuIsOpen(isOpen)}
      >
        <Dropdown.Toggle as={CustomToggle} id="column-selector-dropdown" />
        <Dropdown.Menu
          style={{ paddingLeft: '0.6rem', minWidth: '20rem', fontSize: '1rem', fontWeight: 'normal' }}
        >
          <Dropdown.Header>
            <h5>
              <b>
                {i18n.language === 'gr'
                  ? t('Επιλέξτε τις ορατές στήλες:')
                  : t('Select the visible columns:')}
              </b>
            </h5>
          </Dropdown.Header>
          {table.getAllLeafColumns().map((column) => {
            let columnName: string = column.id;
            if (column.columnDef.header && typeof column.columnDef.header === 'string') {
              columnName = column.columnDef.header;
            }
            if ((column.columnDef as GridColumnDef).columnName) {
              columnName = (column.columnDef as GridColumnDef).columnName!;
            }
            return (
              <div key={column.id} className="px-1">
                <label>
                  <input
                    type="checkbox"
                    checked={column.getIsVisible()}
                    onChange={column.getToggleVisibilityHandler()}
                  />{' '}
                  {columnName}
                </label>
              </div>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export type TableV8ChromeProps<TData extends object> = {
  props: TablePaginatedV8CoreProps<TData>;
  table: Table<TData>;
  displayData: TData[];
  saveColumns: (state: Partial<ColumnPersistenceSnapshot>) => void;
  scrollContainerStyle: CSSProperties;
  handlePageChange: (pageNo: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
  tableStore?: TableStore | null;
  footerElement?: ReactNode;
};

/**
 * TableV8Chrome component
 * @param param0
 * @returns
 */
export function TableV8Chrome<TData extends object>({
  props,
  table,
  displayData,
  saveColumns,
  scrollContainerStyle,
  handlePageChange,
  handlePageSizeChange,
  tableStore = null,
}: TableV8ChromeProps<TData>) {
  const { t } = useTranslation();
  const {
    dataCount,
    loading = false,
    topBorderRadius = true,
    showFooter = false,
    showGridFooter = true,
    hidePagination = false,
    enableColumnSelector = true,
    onExport,
    injectFooterElement,
    onRowClick = null,
    tableProps = defTableProps,
    headerGroupProps = defHeaderGroupProps,
    bodyProps = null,
    rowProps = () => ({}),
    cellProps = null,
  } = props;

  return (
    <div className="d-flex flex-column h-100">
      <LoadingOverlay active={loading} spinner text={t('Παρακαλώ περιμένετε...')}>
        {displayData ? (
          <div
            className="tableV8-container d-flex flex-column h-100"
            style={{
              border: '1px solid rgba(0,0,0,0.176)',
              borderTopStyle: 'none',
              borderRadius: topBorderRadius ? '0.25rem ' : '0.0rem 0.0rem 0.25rem 0.25rem',
            }}
          >
            <div className="tableV8-scroll d-flex flex-column h-100" style={scrollContainerStyle}>
              <DndProvider backend={HTML5Backend}>
                <BootstrapTable id="bootstrap-table" {...tableProps}>
                  <thead className="thead-dark hover-space">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id} {...headerGroupProps(headerGroup)}>
                        {headerGroup.headers.map((header, index, headers) => (
                          <DraggableColumnHeader
                            key={header.id}
                            header={header}
                            table={table}
                            index={index}
                            headers={headers}
                            saveColumns={saveColumns}
                            enableColumnSelector={enableColumnSelector}
                          />
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...bodyProps}>
                    {table.getRowModel().rows.map((row, index) => {
                      const rowPropsResult = rowProps(
                        Object.assign(row, { index }) as Row<TData> & { index: number },
                      );
                      const rowStyle = (rowPropsResult as { style?: CSSProperties })?.style || {};
                      return (
                        <tr
                          key={row.id}
                          {...rowPropsResult}
                          onClick={() => {
                            if (tableStore) tableStore.setActiveRowIdx(row.index);
                            if (onRowClick) onRowClick({ table, row });
                          }}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              {...cellProps}
                              style={{
                                ...(rowStyle || {}),
                                ...((cell.column.columnDef as GridColumnDef & { style?: CSSProperties })
                                  .style || {}),
                              }}
                              className={
                                (cell.column.columnDef as GridColumnDef & { className?: string })
                                  .className
                              }
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                  {showFooter && (
                    <tfoot>
                      {table.getFooterGroups().map((footerGroup) => (
                        <tr key={footerGroup.id}>
                          {footerGroup.headers.map((header) => (
                            <th key={header.id} colSpan={header.colSpan}>
                              {header.isPlaceholder
                                ? null
                                : flexRender(header.column.columnDef.footer, header.getContext())}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </tfoot>
                  )}
                </BootstrapTable>
                <div style={{ flexGrow: 4 }} />
              </DndProvider>
            </div>
            {showGridFooter && (
              <GridFooter
                pageCount={table.getPageCount()}
                pageIndex={table.getState().pagination.pageIndex}
                pageSize={table.getState().pagination.pageSize}
                recordCount={dataCount}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onExport={onExport}
                hidePagination={hidePagination}
                injectFooterElement={injectFooterElement}
              />
            )}
          </div>
        ) : (
          <div style={{ height: '200px' }} />
        )}
      </LoadingOverlay>
    </div>
  );
}
